/**
 * The modal.
 *
 * `design/DESIGN-SYSTEM.md` §2, verbatim: "max-width 520px, radius 16px,
 * padding 28px, white, `--mdx-shadow-modal`, backdrop `--mdx-backdrop`. Title
 * 20px 700. Actions bottom-right, secondary then primary, 12px gap."
 *
 * `settings-members.html` draws no modal — the "Invite someone" button has no
 * drawn destination in any of its five states, which Q-020 parks. This is the
 * conservative reading of that gap: the design system specifies a modal in full,
 * so the invite form is built to that spec rather than to an invented shape.
 *
 * Behaviour is hand-written for the reason `shell/Menu.tsx` gives — Escape
 * closes, the backdrop closes, focus moves in on open and back to whatever
 * opened it on close. ARCH §7.3 wants headless primitives for exactly this, and
 * Epic 6 is the story with a real reason to bring one in.
 *
 * Story 4.1.
 */
import { useEffect, useRef, type ReactNode } from 'react'

export function Modal({
  title,
  subtitle,
  onClose,
  children,
  actions,
  footnote,
  dismissible = true,
  closeMark = true,
  balance = false,
}: {
  title: string
  /** Beneath the title, 15px slate — `settings-workspaces.html`'s access panel. */
  subtitle?: ReactNode
  onClose: () => void
  children: ReactNode
  /** Bottom-right, secondary then primary — the caller supplies both. */
  actions: ReactNode
  /**
   * One 13px slate line beneath the actions, right-aligned — `lead-detail.html`'s
   * override: "We keep the original score alongside your correction." Story 22.4.
   */
  footnote?: ReactNode
  /**
   * Whether the person may leave without the action: the ✕, Escape and the
   * backdrop. FALSE for a dialog that shows something once and must be
   * acknowledged — `settings-profile.html`'s `codes-new` draws "No Cancel and no
   * close mark", because the new recovery codes exist from the moment it opens
   * and are shown nowhere else (Story 24.3, DECISION-046).
   */
  dismissible?: boolean
  /**
   * The ✕, where a dismissible dialog draws one. The drawings decide:
   * `settings-workspaces.html`'s access panel draws it, and
   * `settings-profile.html`'s two-factor dialogs — each with a Cancel of its own
   * — draw none (Story 24.3). Escape and the backdrop still dismiss either way.
   */
  closeMark?: boolean
  /**
   * `text-wrap: balance` on the title. M3's drawings set it on every dialog —
   * `editor-build`'s five publish states, `funnels-list`'s four — and the
   * earlier screens' dialogs set it on none, so it is the drawing's to ask for
   * rather than the component's to impose (Story 25.4).
   */
  balance?: boolean
}) {
  const panel = useRef<HTMLDivElement>(null)
  const returnTo = useRef<HTMLElement | null>(null)

  useEffect(() => {
    returnTo.current = document.activeElement as HTMLElement | null
    /**
     * The first FIELD, not the first focusable — somebody who opened a form to
     * type in it should be able to type.
     *
     * Two queries rather than one selector list: a selector list matches in
     * DOCUMENT order, and the close ✕ sits above the body, so the one list put
     * the cursor on Close every time. Found by the screenshot pass.
     */
    const panelEl = panel.current
    const field = panelEl?.querySelector<HTMLElement>('input, select, textarea')
    ;(field ?? panelEl?.querySelector<HTMLElement>('button'))?.focus()

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && dismissible) onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      returnTo.current?.focus()
    }
  }, [onClose, dismissible])

  return (
    /**
     * TOP-ANCHORED, not centred. `settings-workspaces.html` places the panel at
     * `padding: 64px 20px` with `align-items: flex-start`, because a long
     * access list would otherwise grow off both edges at once. The body scrolls
     * and the header and actions stay.
     */
    <div className="fixed inset-0 z-30 flex items-start justify-center overflow-y-auto px-mdx-20 py-mdx-64">
      {dismissible ? (
        <button
          type="button"
          aria-label="Close"
          tabIndex={-1}
          onClick={onClose}
          className="fixed inset-0 cursor-default border-0 bg-mdx-backdrop"
        />
      ) : (
        // No button where nothing happens: a backdrop that cannot dismiss is
        // not announced as one that can.
        <div aria-hidden="true" className="fixed inset-0 bg-mdx-backdrop" />
      )}
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative flex max-h-full w-full max-w-mdx-modal flex-col gap-mdx-20 rounded-mdx-card bg-mdx-white p-mdx-28 shadow-mdx-modal"
      >
        <div className="flex shrink-0 items-start gap-mdx-16">
          <span className="flex min-w-0 flex-1 flex-col gap-mdx-4">
            <h2
              className={`m-0 text-mdx-20 font-mdx-bold leading-mdx-heading tracking-mdx-verdict${balance ? ' text-balance' : ''}`}
            >
              {title}
            </h2>
            {subtitle ? <span className="text-mdx-15 text-mdx-slate">{subtitle}</span> : null}
          </span>
          {dismissible && closeMark ? (
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="flex size-mdx-icon-button shrink-0 cursor-pointer items-center justify-center rounded-mdx-control-sm border border-mdx-line bg-mdx-white text-mdx-13 text-mdx-slate transition-colors duration-mdx-swift ease-mdx-swift hover:bg-mdx-ground"
            >
              ✕
            </button>
          ) : null}
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-mdx-20 overflow-y-auto">{children}</div>
        <div className="flex shrink-0 flex-col gap-mdx-10">
          <div className="flex flex-wrap justify-end gap-mdx-12">{actions}</div>
          {footnote ? <span className="self-end text-mdx-13 text-mdx-slate">{footnote}</span> : null}
        </div>
      </div>
    </div>
  )
}
